import { useState } from "react";
import { Play, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { useApi, useFetch, useMe, errMsg, fmtDate } from "../lib/api";
import { Btn, Card, PageHeader, StateBadge } from "../components/common";

export default function Discover() {
  const api = useApi();
  const { isAdmin } = useMe();
  const { data, reload } = useFetch("/connectors");
  const { data: runs, reload: reloadRuns } = useFetch("/jobs/runs?job=discovery");
  const [busy, setBusy] = useState(false);

  const run = async (connectors) => {
    setBusy(true);
    try {
      await api.post("/discovery/run", { connectors });
      toast.success("Discovery started. Results appear below when the run finishes.");
      setTimeout(() => { reload(); reloadRuns(); setBusy(false); }, 15000);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Discover Leads" subtitle="Each source is a separate connector using official APIs, public feeds or pages you add. A source is only marked operational after a successful scan.">
        <Btn variant="ghost" onClick={() => { reload(); reloadRuns(); }} data-testid="refresh-connectors"><RefreshCw size={14} /> Refresh</Btn>
        {isAdmin && <Btn onClick={() => run(null)} disabled={busy} data-testid="run-all-discovery"><Play size={14} /> {busy ? "Running…" : "Run all connectors now"}</Btn>}
      </PageHeader>

      <Card title="Active connectors" testid="connectors-card">
        <div className="overflow-x-auto"><table className="dm-table w-full">
          <thead><tr><th>Connector</th><th>Type</th><th>Access method</th><th>Cost</th><th>Status</th><th>Last successful scan</th><th>Last scan counts</th><th></th></tr></thead>
          <tbody>{(data?.connectors || []).map((c) => (
            <tr key={c.key} data-testid={`connector-${c.key}`}>
              <td className="font-medium">{c.name}{c.last_error && <div className="mt-1 text-xs text-red-600">{c.last_error}</div>}</td>
              <td className="text-xs">{c.type}</td>
              <td className="text-xs text-slate-600">{c.access}</td>
              <td className="text-xs text-slate-600">{c.cost}</td>
              <td><StateBadge s={c.state} testid={`connector-state-${c.key}`} /></td>
              <td className="text-xs">{fmtDate(c.last_success)}</td>
              <td className="font-mono text-[11px] text-slate-600">{c.last_counts ? `fetched ${c.last_counts.fetched} · new ${c.last_counts.new_leads} · dup ${c.last_counts.duplicates}` : "—"}</td>
              <td>{isAdmin && !c.missing.length && c.state !== "not_configured" && <Btn variant="ghost" className="h-7 px-2 text-xs" disabled={busy} onClick={() => run([c.key])} data-testid={`run-connector-${c.key}`}>Run</Btn>}</td>
            </tr>))}</tbody>
        </table></div>
        <p className="mt-3 text-xs text-slate-500">Company career/partner page URLs, RSS feeds (e.g. Google Alerts “Deliver to RSS”), search keywords and country targets are configured in Settings → Discovery.</p>
      </Card>

      <Card title="Restricted sources — not automated" className="mt-6" testid="restricted-card">
        <div className="grid gap-3 md:grid-cols-2">{(data?.restricted || []).map((r) => (
          <div key={r.key} data-testid={`restricted-${r.key}`} className="flex gap-3 rounded-md border border-slate-200 bg-slate-50 p-3">
            <ShieldAlert size={16} className="mt-0.5 shrink-0 text-slate-500" />
            <div><div className="flex flex-wrap items-center gap-2 text-sm font-medium">{r.name}<StateBadge s={r.status} /></div><p className="mt-1 text-xs text-slate-600">{r.reason}</p></div>
          </div>))}</div>
      </Card>

      <Card title="Discovery run history" className="mt-6" testid="discovery-runs">
        <div className="overflow-x-auto"><table className="dm-table w-full">
          <thead><tr><th>Started</th><th>Trigger</th><th>Status</th><th>Sources</th><th className="text-right">Fetched</th><th className="text-right">Genuine new</th><th className="text-right">Duplicates</th><th className="text-right">Filtered</th><th>Errors</th></tr></thead>
          <tbody>{(runs || []).map((r) => (
            <tr key={r.id}>
              <td className="text-xs">{fmtDate(r.started_at)}</td><td className="text-xs">{r.trigger}</td><td><StateBadge s={r.status} /></td>
              <td className="max-w-xs text-xs">{(r.stats?.sources_searched || []).join(", ") || r.stats?.skipped || "—"}</td>
              <td className="text-right font-mono">{r.stats?.fetched ?? "—"}</td><td className="text-right font-mono">{r.stats?.new_leads ?? "—"}</td>
              <td className="text-right font-mono">{r.stats?.duplicates ?? "—"}</td><td className="text-right font-mono">{r.stats?.filtered_irrelevant ?? "—"}</td>
              <td className="max-w-xs text-xs text-red-600">{(r.errors || []).join("; ")}</td>
            </tr>))}</tbody>
        </table></div>
      </Card>
    </div>
  );
}
